import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4f8d1127-7831-43ae-8d3d-944f658ba2e9/artifacts/6bknvtgo_logo.png';

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '', confirmPassword: '' });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(loginData.email, loginData.password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await register(registerData.username, registerData.email, registerData.password);
      toast.success('Registration successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-emerald-950 py-12 px-4" data-testid="admin-login-page">
      <motion.div
        className="max-w-md w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="RENVITEK" className="h-16 mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-bold text-emerald-900 dark:text-white">
            Admin Panel
          </h1>
          <p className="text-slate-600 dark:text-white/70 mt-2">
            Access the CRM dashboard
          </p>
        </div>

        <div className="bg-white dark:bg-emerald-900 shadow-xl p-8">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login" className="flex items-center gap-2" data-testid="login-tab">
                <LogIn size={16} />
                Login
              </TabsTrigger>
              <TabsTrigger value="register" className="flex items-center gap-2" data-testid="register-tab">
                <UserPlus size={16} />
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    placeholder="admin@renvitek.in"
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-3"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Username
                  </label>
                  <Input
                    type="text"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                    required
                    placeholder="Your name"
                    data-testid="register-username-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    placeholder="admin@renvitek.in"
                    data-testid="register-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    placeholder="Create a password"
                    data-testid="register-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                    Confirm Password
                  </label>
                  <Input
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    required
                    placeholder="Confirm your password"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-3"
                  data-testid="register-submit-btn"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-sm text-slate-500 dark:text-white/50 mt-6">
          <Link to="/" className="hover:text-emerald-600">
            ← Back to Website
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
